// Companies.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box, Button, Typography, Dialog, DialogActions, DialogContent, DialogTitle,
  useMediaQuery, useTheme, Alert, Snackbar, Stack
} from "@mui/material";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import { useCompanies } from "../../../hooks/useCompanies";
import CompanyTable from "../../../components/CompanyTable";
import BulkCompanyPermissionsDialog from "../../../components/BulkCompanyPermissionsDialog";
import AddCompanyForm from "../../../components/AddCompanyForm";
import { usePageContext } from "../../../context/PageContext";
import { useTranslation } from "react-i18next";

const Companies = () => {
  const { t } = useTranslation("settings");
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [bulkPermOpen, setBulkPermOpen] = useState(false);
  const [bulkPermIds, setBulkPermIds] = useState([]);
  const [companyTableKey, setCompanyTableKey] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    updateMutation,
    deleteMutation,
    isLoading,
    isFetching,
    isRefetching,
    getById,
  } = useCompanies(currentPage, pageSize, search);

  useEffect(() => {
    if (error) setErrorMessage("Erro ao carregar empresas. Por favor, tente novamente.");
    else setErrorMessage(null);
  }, [error]);

  // debounce de busca
  useEffect(() => {
    const t = setTimeout(() => setSearch(String(searchInput ?? "").trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // EDITAR -> carrega detalhe antes
  const handleEditCompany = useCallback(async (idOrRow) => {
    try {
      setErrorMessage(null);
      setIsProcessing(true);
      const id = typeof idOrRow === "object" && idOrRow !== null ? idOrRow.id : idOrRow;
      if (!id) { setErrorMessage("ID inválido para edição."); return; }
      const detail = await getById(id);
      const projeto = detail?.projeto || {};
      const fullCompany = {
        ...projeto,
        id: projeto.id ?? id,
        regioes: Array.isArray(detail?.regioes) ? detail.regioes : [],
        cepRanges: Array.isArray(detail?.cepRanges) ? detail.cepRanges : [],
      };
      setSelectedCompany(fullCompany);
    } catch (e) {
      setErrorMessage(e?.message || "Falha ao carregar dados da empresa.");
    } finally {
      setIsProcessing(false);
    }
  }, [getById]);

  const handleDeleteCompany = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const confirmDeleteCompany = () => {
    if (!confirmDelete) return;
    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setSnack({ open: true, message: "Empresa excluída com sucesso.", severity: "success" });
        setConfirmDelete(null);
        refetch();
      },
      onError: (e) => {
        setErrorMessage(e?.response?.data?.message || e?.message || "Erro ao excluir a empresa.");
        setSnack({ open: true, message: "Falha ao excluir.", severity: "error" });
      },
      onSettled: () => setIsProcessing(false),
    });
  };

  const handleToggleStatus = useCallback((row) => {
    const newStatus = row.statusRaw === 1 ? 0 : 1;
    setIsProcessing(true);
    updateMutation.mutate(
      { projeto: { id: row.id, status: newStatus } },
      {
        onSuccess: () => {
          setSnack({
            open: true,
            message: `Empresa ${newStatus === 1 ? "ativada" : "inativada"} com sucesso.`,
            severity: "success",
          });
          refetch();
        },
        onError: () =>
          setSnack({ open: true, message: "Falha ao atualizar status.", severity: "error" }),
        onSettled: () => setIsProcessing(false),
      }
    );
  }, [updateMutation, refetch]);

  const handleDeleteSelected = async (ids) => {
    if (!ids?.length) return;
    setIsProcessing(true);
    setErrorMessage(null);
    const results = await Promise.allSettled(
      ids.map((id) => deleteMutation.mutateAsync(id))
    );
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length > 0) {
      setErrorMessage(
        failed.length === ids.length
          ? "Erro ao excluir as empresas."
          : `${failed.length} de ${ids.length} exclusão(ões) falharam.`
      );
      setSnack({ open: true, message: "Falha em algumas exclusões.", severity: "error" });
    } else {
      setSnack({ open: true, message: "Empresas excluídas com sucesso.", severity: "success" });
    }
    refetch();
    setIsProcessing(false);
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);

  const handleCloseForm = () => {
    setSelectedCompany(null);
    setErrorMessage(null);
  };

  const busy = isLoading || isFetching || isRefetching || isProcessing;
  const pagination = data?.pagination || { total: 0, page: 1, totalPages: 1 };

  const companiesById = useMemo(() => {
    const m = {};
    for (const c of data?.companies || []) {
      if (c?.id != null) m[Number(c.id)] = c.nome || "";
    }
    return m;
  }, [data?.companies]);

  const bulkPermissionsAction = useCallback(
    ({ selectedRows }) => (
      <Button
        variant="outlined"
        color="primary"
        startIcon={<SecurityRoundedIcon />}
        onClick={() => {
          setBulkPermIds((selectedRows || []).map(Number).filter(Boolean));
          setBulkPermOpen(true);
        }}
        sx={{ textTransform: "uppercase" }}
      >
        {t("companyTable.bulkPermissions")}
      </Button>
    ),
    [t]
  );

  const headerRight = useMemo(
    () => (
      <Stack direction={isMobile ? "column" : "row"} spacing={1} alignItems="center">
        <Button variant="contained" color="primary" fullWidth={isMobile} onClick={() => setSelectedCompany({})}>
          {t("companiesPage.addCompany")}
        </Button>
      </Stack>
    ),
    [isMobile, t]
  );

  return (
    <Box sx={{ pt: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>{t("companiesPage.title")}</Typography>
        {headerRight}
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {selectedCompany ? (
        <AddCompanyForm
          key={selectedCompany?.id ?? "new"}
          company={selectedCompany}
          isLoading={busy}
          onClose={handleCloseForm}
          onSubmit={async (payload) => {
            setIsProcessing(true);
            setErrorMessage(null);
            try {
              return await updateMutation.mutateAsync(payload);
            } catch (e) {
              setErrorMessage(e?.response?.data?.message || e?.message || "Erro ao salvar a empresa.");
              setSnack({ open: true, message: "Falha ao salvar.", severity: "error" });
              throw e;
            } finally {
              setIsProcessing(false);
            }
          }}
          onSaved={({ aclApplyFailed } = {}) => {
            setSnack({
              open: true,
              message: aclApplyFailed
                ? t("addCompanyForm.successSavedAclWarning")
                : t("addCompanyForm.successSaved"),
              severity: aclApplyFailed ? "warning" : "success",
            });
            refetch();
            handleCloseForm();
          }}
        />
      ) : (
        <CompanyTable
          key={companyTableKey}
          companies={data?.companies || []}
          rowCount={pagination.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={(term) => setSearchInput(term == null ? "" : String(term))}
          isLoading={busy}
          onEditCompany={handleEditCompany}
          onDeleteCompany={handleDeleteCompany}
          onDeleteSelected={handleDeleteSelected}
          onToggleStatus={handleToggleStatus}
          bulkActions={bulkPermissionsAction}
        />
      )}

      <BulkCompanyPermissionsDialog
        open={bulkPermOpen}
        onClose={() => {
          setBulkPermOpen(false);
          setBulkPermIds([]);
        }}
        companyIds={bulkPermIds}
        companiesById={companiesById}
        onSuccess={({ failed, total }) => {
          refetch();
          setCompanyTableKey((k) => k + 1);
          if (failed === 0) {
            setSnack({
              open: true,
              message: t("companyTable.bulkPermissionsSuccess", { count: total }),
              severity: "success",
            });
          } else {
            setSnack({
              open: true,
              message: t("companyTable.bulkPermissionsPartial", { ok: total - failed, total }),
              severity: "warning",
            });
          }
        }}
      />

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Confirmação de Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing ? "Processando exclusão..." : "Tem certeza de que deseja excluir esta empresa?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
              <Button color="error" onClick={confirmDeleteCompany}>Excluir</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.message}
      />
    </Box>
  );
};

export default Companies;
